import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFamilyContext } from '../context/FamilyContext';

const { FiX, FiActivity, FiUsers, FiCheck } = FiIcons;

export default function MedicalInfoModal({ info, onClose, onSave }) {
  const { state } = useFamilyContext();
  const { familyMembers } = state;
  const [formData, setFormData] = useState({
    type: 'medication',
    title: '',
    description: '',
    dosage: '',
    frequency: '',
    people: []
  });

  useEffect(() => {
    if (info) {
      setFormData(info);
    }
  }, [info]);

  const togglePerson = (personId) => {
    setFormData(prev => {
      const people = prev.people || [];
      if (people.includes(personId)) {
        return { ...prev, people: people.filter(id => id !== personId) };
      } else {
        return { ...prev, people: [...people, personId] };
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden z-10"
        >
          <div className="p-6 overflow-y-auto max-h-[90vh]">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
                <SafeIcon icon={FiActivity} className="w-5 h-5 mr-2 text-red-500" />
                {info ? 'Medizinische Info bearbeiten' : 'Neue Medizinische Info'}
              </h3>
              <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 transition-colors">
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 dark:text-gray-300">Typ</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-red-500 outline-none transition-all"
                >
                  <option value="medication">Medikation</option>
                  <option value="allergy">Allergie</option>
                  <option value="condition">Erkrankung</option>
                  <option value="other">Sonstiges</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 dark:text-gray-300">Titel / Name</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-red-500 outline-none transition-all"
                  placeholder="z.B. Aspirin oder Erdnussallergie"
                />
              </div>

              {formData.type === 'medication' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 dark:text-gray-300">Dosis</label>
                    <input
                      type="text"
                      value={formData.dosage}
                      onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-red-500 outline-none transition-all"
                      placeholder="z.B. 100mg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 dark:text-gray-300">Häufigkeit</label>
                    <input
                      type="text"
                      value={formData.frequency}
                      onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-red-500 outline-none transition-all"
                      placeholder="z.B. morgens"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1 dark:text-gray-300">Beschreibung / Notizen</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  className="w-full border border-gray-200 rounded-lg p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-red-500 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 dark:text-gray-300 flex items-center">
                   <SafeIcon icon={FiUsers} className="w-4 h-4 mr-1" /> Betroffene Personen
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {familyMembers.map(member => (
                    <div 
                      key={member.id}
                      onClick={() => togglePerson(member.id)}
                      className={`cursor-pointer border rounded-lg p-2 flex items-center space-x-2 transition-all ${
                        (formData.people || []).includes(member.id) 
                          ? 'bg-red-50 border-red-500 text-red-700 dark:bg-red-900/30 dark:text-red-200' 
                          : 'hover:bg-gray-50 border-gray-200 dark:hover:bg-gray-700 dark:border-gray-600 dark:text-gray-300'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                        (formData.people || []).includes(member.id) ? 'bg-red-500 border-red-500' : 'border-gray-400'
                      }`}>
                         {formData.people?.includes(member.id) && <SafeIcon icon={FiCheck} className="w-3 h-3 text-white" />}
                      </div>
                      <span className="text-sm font-medium">{member.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-8">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2.5 text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 transition-colors font-semibold"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="px-8 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all shadow-lg shadow-red-500/30 font-semibold"
                >
                  Speichern
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}